import React, { useState } from 'react';
import { Box, Button, Alert, Typography, CircularProgress, Stack } from '@mui/material';
import { CreditCard as CreditCardIcon, Lock as LockIcon } from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY, STRIPE_ENABLED } from '../../config/stripe';

const stripePromise = STRIPE_ENABLED && STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

/**
 * Card payment form for public quote/invoice pages (token link, no login).
 * The charge amount always comes from the server via the pay-intent endpoint.
 *
 * Props:
 *   token        – the signed document token from the URL
 *   documentType – 'INVOICE' | 'QUOTE'
 *   quotationId  – required for QUOTE (used to convert to a booking after payment)
 *   amount       – display-only amount (server re-derives the real one)
 *   onPaid(documentOrBooking) – called after successful payment + confirmation
 */
const PayForm = ({ token, documentType, quotationId, amount, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    try {
      // 1. Server-priced intent
      const intentRes = await fetch(`${apiBase}/api/public/documents/${token}/pay-intent`, { method: 'POST' });
      const intentData = await intentRes.json();
      if (!intentRes.ok) throw new Error(intentData.error || 'Unable to start payment.');

      // 2. Charge the card
      const result = await stripe.confirmCardPayment(intentData.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) }
      });
      if (result.error) throw new Error(result.error.message);

      // 3. Confirm server-side
      if (documentType === 'INVOICE') {
        const confirmRes = await fetch(`${apiBase}/api/public/documents/${token}/confirm-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId: intentData.paymentIntentId })
        });
        const confirmData = await confirmRes.json();
        if (!confirmRes.ok) throw new Error(confirmData.error || 'Payment made but confirmation failed — please contact us.');
        onPaid(confirmData.document);
      } else {
        // QUOTE: convert to a paid booking via the existing public endpoint
        const bookRes = await fetch(`${apiBase}/api/bookings/create-from-quotation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quotationId, stripePaymentIntentId: intentData.paymentIntentId })
        });
        const bookData = await bookRes.json();
        if (!bookRes.ok) throw new Error(bookData.error || 'Payment made but booking failed — please contact us.');
        onPaid(bookData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <Stack spacing={2}>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                invalid: { color: '#c23d4b' }
              }
            }}
            onChange={(e) => setCardComplete(e.complete)}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={processing || !cardComplete || !stripe}
          startIcon={processing ? <CircularProgress size={20} /> : <CreditCardIcon />}
        >
          {processing ? 'Processing…' : `Pay $${Number(amount || 0).toFixed(2)}`}
        </Button>

        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <LockIcon color="success" fontSize="small" />
          <Typography variant="caption" color="text.secondary">
            Secure payment powered by Stripe
          </Typography>
        </Stack>
      </Stack>
    </form>
  );
};

const PublicDocumentPayForm = (props) => {
  if (!stripePromise) return null; // Stripe disabled — page stays view-only
  return (
    <Elements stripe={stripePromise}>
      <PayForm {...props} />
    </Elements>
  );
};

export default PublicDocumentPayForm;
